import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Save, Trash2, Plus, Home as HomeIcon, Image as ImageIcon,
  MessageSquare, Settings, Upload, Users, ExternalLink,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";

const TABS = [
  { id: "content", label: "Site Content", icon: Settings },
  { id: "services", label: "Services", icon: HomeIcon },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "testimonials", label: "Testimonials", icon: MessageSquare },
  { id: "leads", label: "Quote Requests", icon: Users },
];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { site, gallery, reload } = useSite();
  const navigate = useNavigate();
  const [tab, setTab] = useState("content");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);

  useEffect(() => { if (site) setDraft({ ...site }); }, [site]);
  useEffect(() => { if (tab === "leads") fetchLeads(); }, [tab]);

  const fetchLeads = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) { toast.error("Could not load leads"); }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const saveSite = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = { ...draft, ...extra };
      const { data } = await api.put("/site", payload);
      setDraft({ ...data });
      await reload();
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <div className="min-h-screen bg-neutral-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-neutral-950 text-white min-h-screen sticky top-0 flex flex-col">
        <div className="p-6 border-b border-neutral-800">
          {site?.logo_url && (
            <img src={site.logo_url} alt="logo" className="h-14 w-auto bg-white p-1" />
          )}
          <p className="mt-3 text-xs uppercase tracking-widest text-neutral-500">Admin Panel</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 font-heading uppercase text-xs tracking-widest text-left transition-colors ${
                  active ? "bg-[#DC2626] text-white" : "text-neutral-300 hover:bg-neutral-900"
                }`}
              >
                <t.icon size={16} strokeWidth={2.5} />
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-neutral-800 space-y-2">
          <Link
            data-testid="view-site-link"
            to="/"
            target="_blank"
            className="w-full flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-white"
          >
            <ExternalLink size={14} />
            View site
          </Link>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#DC2626]"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 lg:p-12 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
              {TABS.find((t) => t.id === tab)?.label}
            </h1>
            <p className="text-neutral-500 text-sm mt-1">Signed in as {user?.email}</p>
          </div>
        </div>

        {tab === "content" && (
          <ContentEditor draft={draft} setDraft={setDraft} onSave={() => saveSite()} saving={saving} />
        )}
        {tab === "services" && (
          <ServicesEditor draft={draft} setDraft={setDraft} onSave={() => saveSite()} saving={saving} />
        )}
        {tab === "gallery" && (
          <GalleryEditor gallery={gallery} reload={reload} />
        )}
        {tab === "testimonials" && (
          <TestimonialsEditor draft={draft} setDraft={setDraft} onSave={() => saveSite()} saving={saving} />
        )}
        {tab === "leads" && (
          <LeadsView leads={leads} reload={fetchLeads} />
        )}
      </main>
    </div>
  );
}

// ---------- Content editor ----------
function Field({ label, value, onChange, type = "text", multiline = false, testId }) {
  return (
    <div>
      <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">
        {label}
      </label>
      {multiline ? (
        <textarea
          data-testid={testId}
          rows={4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 border-2 border-neutral-200 bg-white focus:border-[#DC2626] focus:outline-none resize-none"
        />
      ) : (
        <input
          data-testid={testId}
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 border-2 border-neutral-200 bg-white focus:border-[#DC2626] focus:outline-none"
        />
      )}
    </div>
  );
}

function ImageInput({ label, value, onChange, testId }) {
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3 MB)"); return; }
    const url = await fileToDataUrl(f);
    onChange(url);
  };
  return (
    <div>
      <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">{label}</label>
      <div className="flex gap-3 items-start">
        {value && (
          <img src={value} alt="" className="h-24 w-24 object-cover border-2 border-neutral-200 bg-white" />
        )}
        <div className="flex-1 space-y-2">
          <input
            data-testid={`${testId}-url`}
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste image URL…"
            className="w-full px-4 py-2 border-2 border-neutral-200 bg-white focus:border-[#DC2626] focus:outline-none text-sm"
          />
          <label className="btn-secondary-cta text-xs cursor-pointer" style={{ padding: "0.5rem 1rem" }}>
            <Upload size={14} /> Upload file
            <input data-testid={`${testId}-file`} type="file" accept="image/*" onChange={upload} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
}

function ContentEditor({ draft, setDraft, onSave, saving }) {
  const set = (k) => (v) => setDraft({ ...draft, [k]: v });
  return (
    <div className="bg-white p-8 border border-neutral-200 space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Field testId="input-business-name" label="Business Name" value={draft.business_name} onChange={set("business_name")} />
        <Field testId="input-tagline" label="Tagline" value={draft.tagline} onChange={set("tagline")} />
      </div>

      <Field testId="input-hero-title" label="Hero Title" value={draft.hero_title} onChange={set("hero_title")} />
      <Field testId="input-hero-subtitle" label="Hero Subtitle" value={draft.hero_subtitle} onChange={set("hero_subtitle")} multiline />

      <div className="grid md:grid-cols-2 gap-6">
        <ImageInput testId="input-logo" label="Logo (shown dynamically in hero)" value={draft.logo_url} onChange={set("logo_url")} />
        <ImageInput testId="input-hero-bg" label="Hero background image" value={draft.hero_background} onChange={set("hero_background")} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Field testId="input-cta-primary" label="Primary CTA" value={draft.hero_cta_primary} onChange={set("hero_cta_primary")} />
        <Field testId="input-cta-secondary" label="Secondary CTA" value={draft.hero_cta_secondary} onChange={set("hero_cta_secondary")} />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Field testId="input-phone" label="Phone (display)" value={draft.phone} onChange={set("phone")} />
        <Field testId="input-phone-raw" label="Phone (dial)" value={draft.phone_raw} onChange={set("phone_raw")} />
        <Field testId="input-email" label="Email" value={draft.email} onChange={set("email")} />
      </div>

      <Field testId="input-service-area" label="Service Area" value={draft.service_area} onChange={set("service_area")} />
      <Field testId="input-about-title" label="About Title" value={draft.about_title} onChange={set("about_title")} />
      <Field testId="input-about-text" label="About Text" value={draft.about_text} onChange={set("about_text")} multiline />

      <div className="grid md:grid-cols-3 gap-6">
        <Field testId="input-facebook" label="Facebook URL" value={draft.facebook} onChange={set("facebook")} />
        <Field testId="input-instagram" label="Instagram URL" value={draft.instagram} onChange={set("instagram")} />
        <Field testId="input-whatsapp" label="WhatsApp URL" value={draft.whatsapp} onChange={set("whatsapp")} />
      </div>

      <div className="pt-4 border-t border-neutral-200">
        <button
          data-testid="save-content-btn"
          onClick={onSave}
          disabled={saving}
          className="btn-primary-cta"
        >
          <Save size={16} strokeWidth={2.5} />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

// ---------- Services editor ----------
function ServicesEditor({ draft, setDraft, onSave, saving }) {
  const services = draft.services || [];
  const update = (i, k, v) => {
    const next = [...services];
    next[i] = { ...next[i], [k]: v };
    setDraft({ ...draft, services: next });
  };
  const add = () => {
    setDraft({
      ...draft,
      services: [...services, { id: crypto.randomUUID(), title: "New Service", description: "", icon: "Hammer", image: "" }],
    });
  };
  const remove = (i) => {
    const next = services.filter((_, idx) => idx !== i);
    setDraft({ ...draft, services: next });
  };

  return (
    <div className="space-y-4">
      {services.map((s, i) => (
        <div key={s.id || i} className="bg-white border border-neutral-200 p-6 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Field testId={`svc-title-${i}`} label="Title" value={s.title} onChange={(v) => update(i, "title", v)} />
            <Field testId={`svc-icon-${i}`} label="Icon (lucide name)" value={s.icon} onChange={(v) => update(i, "icon", v)} />
            <div className="flex items-end">
              <button
                data-testid={`svc-remove-${i}`}
                onClick={() => remove(i)}
                className="text-[#DC2626] hover:text-[#B91C1C] inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest"
              >
                <Trash2 size={16} /> Remove
              </button>
            </div>
          </div>
          <Field testId={`svc-desc-${i}`} label="Description" value={s.description} onChange={(v) => update(i, "description", v)} multiline />
          <ImageInput testId={`svc-img-${i}`} label="Image" value={s.image} onChange={(v) => update(i, "image", v)} />
        </div>
      ))}
      <div className="flex gap-3">
        <button data-testid="add-service-btn" onClick={add} className="btn-secondary-cta">
          <Plus size={16} /> Add service
        </button>
        <button data-testid="save-services-btn" onClick={onSave} disabled={saving} className="btn-primary-cta">
          <Save size={16} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ---------- Gallery editor ----------
function GalleryEditor({ gallery, reload }) {
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ url: "", caption: "", category: "General" });

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error("Delete failed"); }
  };

  const add = async () => {
    if (!newItem.url) return toast.error("Image required");
    setAdding(true);
    try {
      await api.post("/gallery", newItem);
      toast.success("Added");
      setNewItem({ url: "", caption: "", category: "General" });
      reload();
    } catch (e) { toast.error("Add failed"); }
    finally { setAdding(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3 MB)"); return; }
    const url = await fileToDataUrl(f);
    setNewItem((s) => ({ ...s, url }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-neutral-200 p-6">
        <h3 className="font-heading uppercase text-sm tracking-widest mb-4">Add new image</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <ImageInput testId="new-gallery" label="Image" value={newItem.url} onChange={(v) => setNewItem({ ...newItem, url: v })} />
          <div className="space-y-4">
            <Field testId="new-gallery-caption" label="Caption" value={newItem.caption} onChange={(v) => setNewItem({ ...newItem, caption: v })} />
            <Field testId="new-gallery-category" label="Category" value={newItem.category} onChange={(v) => setNewItem({ ...newItem, category: v })} />
          </div>
        </div>
        <button
          data-testid="add-gallery-btn"
          onClick={add}
          disabled={adding}
          className="btn-primary-cta mt-4"
        >
          <Plus size={16} /> {adding ? "Adding…" : "Add to gallery"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {gallery.map((g) => (
          <div key={g.id} data-testid={`admin-gallery-${g.id}`} className="relative group border border-neutral-200 bg-white">
            <img src={g.url} alt={g.caption} className="w-full h-40 object-cover" />
            <div className="p-3">
              <p className="text-xs text-neutral-500 uppercase tracking-widest">{g.category}</p>
              <p className="text-sm truncate">{g.caption}</p>
            </div>
            <button
              data-testid={`gallery-delete-${g.id}`}
              onClick={() => remove(g.id)}
              className="absolute top-2 right-2 bg-black/70 hover:bg-[#DC2626] text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Testimonials editor ----------
function TestimonialsEditor({ draft, setDraft, onSave, saving }) {
  const items = draft.testimonials || [];
  const update = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    setDraft({ ...draft, testimonials: next });
  };
  const add = () => {
    setDraft({
      ...draft,
      testimonials: [...items, { id: crypto.randomUUID(), name: "New Client", location: "", rating: 5, text: "" }],
    });
  };
  const remove = (i) => setDraft({ ...draft, testimonials: items.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4">
      {items.map((t, i) => (
        <div key={t.id || i} className="bg-white border border-neutral-200 p-6 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Field testId={`t-name-${i}`} label="Name" value={t.name} onChange={(v) => update(i, "name", v)} />
            <Field testId={`t-location-${i}`} label="Location" value={t.location} onChange={(v) => update(i, "location", v)} />
            <Field testId={`t-rating-${i}`} label="Rating (1-5)" type="number" value={t.rating} onChange={(v) => update(i, "rating", Number(v) || 5)} />
          </div>
          <Field testId={`t-text-${i}`} label="Testimonial" value={t.text} onChange={(v) => update(i, "text", v)} multiline />
          <button
            data-testid={`t-remove-${i}`}
            onClick={() => remove(i)}
            className="text-[#DC2626] inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest"
          >
            <Trash2 size={16} /> Remove
          </button>
        </div>
      ))}
      <div className="flex gap-3">
        <button data-testid="add-testimonial-btn" onClick={add} className="btn-secondary-cta">
          <Plus size={16} /> Add testimonial
        </button>
        <button data-testid="save-testimonials-btn" onClick={onSave} disabled={saving} className="btn-primary-cta">
          <Save size={16} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ---------- Leads view ----------
function LeadsView({ leads, reload }) {
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error("Delete failed"); }
  };

  if (!leads.length) {
    return (
      <div className="bg-white border border-neutral-200 p-12 text-center">
        <MessageSquare size={40} className="text-neutral-300 mx-auto mb-4" />
        <p className="text-neutral-500">No quote requests yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {leads.map((l) => (
        <div key={l.id} data-testid={`lead-${l.id}`} className="bg-white border border-neutral-200 p-6">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-baseline gap-3">
                <h3 className="font-heading text-lg font-bold uppercase">{l.name}</h3>
                <a href={`tel:${l.phone}`} className="text-[#DC2626] font-medium">{l.phone}</a>
                {l.email && <a href={`mailto:${l.email}`} className="text-neutral-600 text-sm">{l.email}</a>}
              </div>
              {l.service && (
                <p className="text-xs uppercase tracking-widest text-neutral-500 mt-1">
                  Interested in: <span className="text-neutral-900">{l.service}</span>
                </p>
              )}
              {l.message && <p className="mt-3 text-neutral-700">{l.message}</p>}
              <p className="mt-3 text-xs text-neutral-400">
                {new Date(l.created_at).toLocaleString()}
              </p>
            </div>
            <button
              data-testid={`lead-delete-${l.id}`}
              onClick={() => remove(l.id)}
              className="text-neutral-400 hover:text-[#DC2626]"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
